import { AdminApiError, type AdminClient, type DragontigerWeightsOverride } from '@apx/admin-sdk';
import { dragontigerWeightsSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';

export interface DragontigerWeightsPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

// Multiplier buckets shipped by @apx/game-dragontiger CRIT_WEIGHTS. '1' = no crit.
const CRIT_MULTIPLIERS = ['1', '2', '3', '5', '10', '20'] as const;

const DEFAULT_WEIGHTS: DragontigerWeightsOverride = {
  version: 1,
  crit: { '1': 9950, '2': 30, '3': 10, '5': 6, '10': 3, '20': 1 },
};

/**
 * Dragon & Tiger RNG editor — crit-multiplier weights. Rank + suit draws are a
 * uniform fair deck (not tunable); the only RTP lever is the weighted crit
 * table applied to winning payouts. Values are integer RELATIVE weights (not
 * probabilities — the engine normalises by the total). '1' (no crit) must stay
 * positive. Two-admin approval applies. Lifting the high-multiplier tails
 * raises RTP fast — sanity-check against Live RTP below.
 */
export async function renderDragontigerWeightsPage(
  root: HTMLElement,
  deps: DragontigerWeightsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">RNG &amp; RTP — Dragon &amp; Tiger</h1>
    <p class="page-sub">Card rank and suit are a uniform fair deck. The tunable RTP lever is the weighted <strong>crit multiplier</strong> applied to winning payouts. Weights are integer relative weights (engine normalises). Keep <code>×1</code> (no crit) dominant — heavy tails blow RTP past 100%.</p>

    <div class="card">
      <h2>Crit multiplier weights</h2>
      <div class="form-grid">${CRIT_MULTIPLIERS.map(
        (m) => `<label>×${m}${m === '1' ? ' (no crit)' : ''}
          <input data-crit="${m}" type="number" min="0" max="1000000" step="1" />
          <span class="muted" data-prob="${m}">—</span>
        </label>`,
      ).join('')}</div>
      <p class="muted" style="margin-top:8px">
        <span id="crit-total">total weight: 0</span> ·
        <span id="crit-ev">E[crit] per win: —</span>
      </p>
      <div class="row">
        <button id="dw-reset" class="ghost">Reset to defaults</button>
        <button id="dw-save" class="primary">Propose change</button>
        <span id="dw-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="dw-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const critInputs = new Map<string, HTMLInputElement>();
  for (const m of CRIT_MULTIPLIERS) {
    const el = root.querySelector<HTMLInputElement>(`input[data-crit="${m}"]`);
    if (el) {
      critInputs.set(m, el);
      // 'input' keeps derived stats live; 'change' snaps to an integer in
      // [0, 1_000_000] on blur so a stray decimal or overflow can't be proposed.
      el.addEventListener('input', updateDerived);
      el.addEventListener('change', () => {
        const n = Math.round(Number(el.value) || 0);
        el.value = String(Math.min(1_000_000, Math.max(0, n)));
        updateDerived();
      });
    }
  }
  const totalEl = root.querySelector<HTMLElement>('#crit-total')!;
  const evEl = root.querySelector<HTMLElement>('#crit-ev')!;
  const msgEl = root.querySelector<HTMLElement>('#dw-msg')!;
  const saveBtn = root.querySelector<HTMLButtonElement>('#dw-save')!;

  // Mirrors dragontigerWeightsSchema: integer weights ∈ [0, 1_000_000], total
  // must be positive, and ×1 (no-crit) must be > 0 so the deck can ever miss.
  function critValidity(): { ok: boolean; reason: string } {
    const vals = [...critInputs.values()].map((i) => Math.round(Number(i.value) || 0));
    if (vals.some((v) => v < 0 || v > 1_000_000)) {
      return { ok: false, reason: 'each weight must be 0–1,000,000' };
    }
    if (vals.reduce((a, b) => a + b, 0) <= 0) {
      return { ok: false, reason: 'total weight must be positive' };
    }
    if (Math.round(Number(critInputs.get('1')?.value) || 0) <= 0) {
      return { ok: false, reason: '×1 (no crit) must be > 0' };
    }
    return { ok: true, reason: '' };
  }

  function updateDerived(): void {
    let total = 0;
    for (const input of critInputs.values()) total += Number(input.value) || 0;
    let ev = 0;
    for (const [m, input] of critInputs) {
      const w = Number(input.value) || 0;
      const prob = total > 0 ? w / total : 0;
      ev += Number(m) * prob;
      const probEl = root.querySelector<HTMLElement>(`span[data-prob="${m}"]`);
      if (probEl) probEl.textContent = total > 0 ? `${(prob * 100).toFixed(3)}%` : '—';
    }
    totalEl.textContent = `total weight: ${total.toLocaleString()}`;
    totalEl.style.color = total > 0 ? 'var(--success)' : 'var(--error)';
    // E[crit] > ~1.04 starts pushing main-bet RTP toward 100%.
    evEl.textContent = `E[crit] per win: ${ev.toFixed(4)}×`;
    evEl.style.color = ev >= 1.06 ? 'var(--error)' : ev >= 1.04 ? 'var(--warn)' : 'var(--success)';
    const v = critValidity();
    saveBtn.disabled = !v.ok;
    saveBtn.title = v.ok ? '' : v.reason;
    if (!v.ok) totalEl.style.color = 'var(--error)';
  }

  function fillForm(value: DragontigerWeightsOverride): void {
    const crit = value.crit as Record<string, number>;
    for (const [m, input] of critInputs) input.value = String(crit[m] ?? 0);
    updateDerived();
  }
  function readForm(): DragontigerWeightsOverride {
    const crit: Record<string, number> = {};
    for (const [m, input] of critInputs) crit[m] = Math.round(Number(input.value) || 0);
    return { version: 1, crit };
  }

  // Load current effective surface
  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
  const stored = current as Partial<DragontigerWeightsOverride> | null;
  fillForm(stored && stored.crit ? (stored as DragontigerWeightsOverride) : DEFAULT_WEIGHTS);

  root.querySelector<HTMLButtonElement>('#dw-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_WEIGHTS);
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#dw-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = dragontigerWeightsSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'weights',
        parsed.data as DragontigerWeightsOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'weights' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#dw-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — crit weights now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
          const value = fresh.value as Partial<DragontigerWeightsOverride> | null;
          fillForm(value && value.crit ? (value as DragontigerWeightsOverride) : DEFAULT_WEIGHTS);
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
